import { CommonModule } from '@angular/common';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChildren,
  forwardRef,
  input,
} from '@angular/core';
import { FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { SeahorseTemplateDirective } from '@seahorse/common';
import { PrimeTemplate } from 'primeng/api';
import { SelectModule } from 'primeng/select';
import { ShFormControl } from '../form-control/form-control.base';
import { primeSize } from '../models';
import { buildOptionItems, ShOptionAccessor } from './option-items';

/**
 * Single select. The value is the option's `optionValue` (the option itself
 * without one).
 *
 *   <sh-select [options]="ships" optionLabel="name" optionValue="id" [(value)]="shipId" />
 *   <sh-select [options]="ships" [optionLabel]="shipName" filter showClear />
 *
 * `optionLabel` and `optionValue` are a (dotted) field name or a function.
 * Option labels are shown as they are (translate them before passing them
 * in). Custom rendering: `<ng-template shTemplate="item" let-option>` for the
 * list items and `shTemplate="selected"` for the closed control; both get the
 * caller's option. The list is appended to the body.
 */
@Component({
  selector: 'sh-select',
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    SelectModule,
    PrimeTemplate,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ShSelectComponent),
      multi: true,
    },
  ],
  host: { class: 'sh-control', '[class.sh-invalid]': 'isInvalid()' },
  template: `
    <p-select
      *ngIf="selectedTemplate() as selectedTpl; else plain"
      [options]="items()"
      optionLabel="label"
      optionValue="value"
      filterBy="label"
      [ngModel]="value()"
      (ngModelChange)="commit($event)"
      [ngModelOptions]="{ standalone: true }"
      [inputId]="controlId()"
      [placeholder]="placeholder() | translate"
      [filter]="filter()"
      [autofocusFilter]="filter()"
      [showClear]="showClear()"
      [size]="pSize()"
      [readonly]="isReadonly()"
      [disabled]="isDisabled()"
      [ariaLabel]="ariaLabel() | translate"
      [fluid]="true"
      [appendTo]="appendTo()"
      (onBlur)="markTouched()"
    >
      <ng-template pTemplate="selectedItem" let-selected>
        <ng-container
          *ngTemplateOutlet="selectedTpl; context: { $implicit: selected.raw }"
        ></ng-container>
      </ng-template>
      <ng-template pTemplate="item" let-option>
        <ng-container
          *ngIf="itemTemplate() as itemTpl; else defaultItem"
          [ngTemplateOutlet]="itemTpl"
          [ngTemplateOutletContext]="{ $implicit: option.raw }"
        ></ng-container>
        <ng-template #defaultItem>{{ option.label }}</ng-template>
      </ng-template>
    </p-select>

    <ng-template #plain>
      <p-select
        [options]="items()"
        optionLabel="label"
        optionValue="value"
        filterBy="label"
        [ngModel]="value()"
        (ngModelChange)="commit($event)"
        [ngModelOptions]="{ standalone: true }"
        [inputId]="controlId()"
        [placeholder]="placeholder() | translate"
        [filter]="filter()"
        [autofocusFilter]="filter()"
        [showClear]="showClear()"
        [size]="pSize()"
        [readonly]="isReadonly()"
        [disabled]="isDisabled()"
        [ariaLabel]="ariaLabel() | translate"
        [fluid]="true"
        [appendTo]="appendTo()"
        (onBlur)="markTouched()"
      >
        <ng-template pTemplate="item" let-option>
          <ng-container
            *ngIf="itemTemplate() as itemTpl; else defaultItem"
            [ngTemplateOutlet]="itemTpl"
            [ngTemplateOutletContext]="{ $implicit: option.raw }"
          ></ng-container>
          <ng-template #defaultItem>{{ option.label }}</ng-template>
        </ng-template>
      </p-select>
    </ng-template>
  `,
})
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export class ShSelectComponent<T = any> extends ShFormControl<unknown> {
  readonly options = input<readonly T[] | null | undefined>();
  readonly optionLabel = input<ShOptionAccessor<T, string>>();
  readonly optionValue = input<ShOptionAccessor<T, unknown>>();
  readonly filter = input(false, { transform: booleanAttribute });
  readonly showClear = input(false, { transform: booleanAttribute });
  /**
   * Where the overlay is appended: the body by default (no clipping by
   * scrolling containers); `self` keeps it inline, which a control inside a
   * popover or drawer needs, or a click in the overlay counts as an outside
   * click and closes its container.
   */
  readonly appendTo = input<'body' | 'self' | HTMLElement>('body');

  private readonly _templates = contentChildren(SeahorseTemplateDirective, {
    descendants: true,
  });

  // A computed, not a getter: PrimeNG must get the same array until the
  // options (or how they are read) really change.
  protected readonly items = computed(() =>
    buildOptionItems(this.options(), this.optionLabel(), this.optionValue())
  );
  protected readonly pSize = computed(
    () => primeSize(this.size()) as 'small' | 'large'
  );
  protected readonly itemTemplate = computed(
    () => this._templates().find((t) => t.type === 'item')?.templateRef
  );
  protected readonly selectedTemplate = computed(
    () => this._templates().find((t) => t.type === 'selected')?.templateRef
  );
}
